import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Trash2,
  Edit2,
  DollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
  Globe,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  currencyAPI,
  Currency,
  CreateCurrencyData,
  UpdateCurrencyData,
} from "@/services/currencyService";

interface CurrencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onCurrencyCreated?: () => void;
  onCurrencyUpdated?: () => void;
  onCurrencyDeleted?: () => void;
}

interface CurrencyFormData {
  name: string;
  code: string;
  symbol: string;
  is_default: boolean;
  is_active: boolean;
}

export function CurrencyModal({
  isOpen,
  onClose,
  slug,
  onCurrencyCreated,
  onCurrencyUpdated,
  onCurrencyDeleted,
}: CurrencyModalProps) {
  const { toast } = useToast();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState({
    fetch: false,
    save: false,
    delete: false,
  });
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [formData, setFormData] = useState<CurrencyFormData>({
    name: "",
    code: "",
    symbol: "",
    is_default: false,
    is_active: true,
  });

  useEffect(() => {
    if (isOpen && slug) {
      fetchCurrencies();
    }
  }, [isOpen, slug]);

  const fetchCurrencies = async () => {
    try {
      setLoading((prev) => ({ ...prev, fetch: true }));
      const response = await currencyAPI.getCurrencies({
        slug,
        is_active: true,
      });
      setCurrencies(response.data);
    } catch (error: any) {
      toast({
        title: "Error al cargar monedas",
        description: error.message || "No se pudieron cargar las monedas",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  const handleInputChange = (field: keyof CurrencyFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      symbol: "",
      is_default: false,
      is_active: true,
    });
    setEditingCurrency(null);
  };

  const handleSubmit = async () => {
    try {
      if (
        !formData.name.trim() ||
        !formData.code.trim() ||
        !formData.symbol.trim()
      ) {
        toast({
          title: "Campos incompletos",
          description: "Por favor completa todos los campos requeridos",
          variant: "destructive",
        });
        return;
      }

      setLoading((prev) => ({ ...prev, save: true }));

      if (editingCurrency) {
        const updateData: UpdateCurrencyData = {
          slug,
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
          symbol: formData.symbol.trim(),
          is_default: formData.is_default,
          is_active: formData.is_active,
        };

        await currencyAPI.updateCurrency(editingCurrency.id, updateData);

        toast({
          title: "¡Moneda actualizada!",
          description: "La moneda se ha actualizado correctamente",
        });

        onCurrencyUpdated?.();
      } else {
        const createData: CreateCurrencyData = {
          slug,
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
          symbol: formData.symbol.trim(),
          is_default: formData.is_default,
          is_active: formData.is_active,
        };

        await currencyAPI.createCurrency(createData);

        toast({
          title: "¡Moneda creada!",
          description: "La moneda se ha creado correctamente",
        });

        onCurrencyCreated?.();
      }

      resetForm();
      fetchCurrencies();
    } catch (error: any) {
      toast({
        title: editingCurrency
          ? "Error al actualizar moneda"
          : "Error al crear moneda",
        description: error.message || "Ha ocurrido un error",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleEditCurrency = (currency: Currency) => {
    setFormData({
      name: currency.name,
      code: currency.code,
      symbol: currency.symbol,
      is_default: currency.is_default,
      is_active: currency.is_active,
    });
    setEditingCurrency(currency);
  };

  const handleDeleteCurrency = async (currencyId: string) => {
    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      await currencyAPI.deleteCurrency(currencyId, slug);

      toast({
        title: "¡Moneda eliminada!",
        description: "La moneda se ha eliminado correctamente",
      });

      setCurrencies((prev) => prev.filter((c) => c.id !== currencyId));
      setDeleteConfirm(null);
      onCurrencyDeleted?.();
    } catch (error: any) {
      toast({
        title: "Error al eliminar moneda",
        description: error.message || "No se pudo eliminar la moneda",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleSetAsDefault = async (currency: Currency) => {
    try {
      setLoading((prev) => ({ ...prev, save: true }));

      const updatePromises = currencies
        .filter((c) => c.is_default)
        .map((c) =>
          currencyAPI.updateCurrency(c.id, { slug, is_default: false })
        );

      await Promise.all(updatePromises);

      await currencyAPI.updateCurrency(currency.id, { slug, is_default: true });

      toast({
        title: "Moneda predeterminada actualizada",
        description: `${currency.name} (${currency.code}) es ahora la moneda predeterminada`,
      });

      fetchCurrencies();
      onCurrencyUpdated?.();
    } catch (error: any) {
      toast({
        title: "Error al actualizar moneda predeterminada",
        description: error.message || "Ha ocurrido un error",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, save: false }));
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Gestión de Monedas
            </DialogTitle>
            <DialogDescription>
              Administra las monedas disponibles para tu empresa
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        placeholder="Ej: Dólar Americano"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="code">Código *</Label>
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) =>
                          handleInputChange(
                            "code",
                            e.target.value.toUpperCase()
                          )
                        }
                        placeholder="Ej: USD"
                        maxLength={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="symbol">Símbolo *</Label>
                      <Input
                        id="symbol"
                        value={formData.symbol}
                        onChange={(e) =>
                          handleInputChange("symbol", e.target.value)
                        }
                        placeholder="Ej: $"
                        maxLength={5}
                      />
                    </div>

                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="is_default">
                            Moneda predeterminada
                          </Label>
                          <p className="text-sm text-slate-500">
                            {formData.is_default
                              ? "Esta será la moneda por defecto"
                              : "No es la moneda predeterminada"}
                          </p>
                        </div>
                        <Switch
                          id="is_default"
                          checked={formData.is_default}
                          onCheckedChange={(checked) =>
                            handleInputChange("is_default", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="is_active">Estado</Label>
                          <p className="text-sm text-slate-500">
                            {formData.is_active
                              ? "La moneda está activa"
                              : "La moneda está desactivada"}
                          </p>
                        </div>
                        <Switch
                          id="is_active"
                          checked={formData.is_active}
                          onCheckedChange={(checked) =>
                            handleInputChange("is_active", checked)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={resetForm}
                  disabled={loading.save}
                >
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={loading.save}>
                  {loading.save ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : editingCurrency ? (
                    "Actualizar Moneda"
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Crear Moneda
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <Card>
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Monedas Disponibles</span>
                    <Badge variant="outline">
                      {currencies.length} moneda
                      {currencies.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-4">
                  {loading.fetch ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : currencies.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No hay monedas configuradas</p>
                      <p className="text-sm mt-1">Crea tu primera moneda</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {currencies.map((currency) => (
                        <Card key={currency.id} className="overflow-hidden">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-blue-500" />
                                <Badge
                                  variant="outline"
                                  className={
                                    currency.is_default
                                      ? "bg-blue-50 text-blue-700 border-blue-200"
                                      : "bg-slate-50 text-slate-500"
                                  }
                                >
                                  {currency.is_default ? (
                                    <>
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Predeterminada
                                    </>
                                  ) : (
                                    "Moneda"
                                  )}
                                </Badge>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => handleEditCurrency(currency)}
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => setDeleteConfirm(currency.id)}
                                  disabled={loading.delete}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div>
                                <p className="font-medium text-sm">
                                  {currency.name} ({currency.code})
                                </p>
                                <p className="text-sm text-slate-600">
                                  Símbolo: {currency.symbol}
                                </p>
                              </div>

                              <div className="flex items-center justify-between text-sm">
                                <span
                                  className={`text-xs ${
                                    currency.is_active
                                      ? "text-green-600"
                                      : "text-slate-400"
                                  }`}
                                >
                                  {currency.is_active ? (
                                    <span className="flex items-center gap-1">
                                      <CheckCircle className="h-3 w-3" />
                                      Activa
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <XCircle className="h-3 w-3" />
                                      Inactiva
                                    </span>
                                  )}
                                </span>

                                {!currency.is_default && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-xs"
                                    onClick={() => handleSetAsDefault(currency)}
                                    disabled={loading.save}
                                  >
                                    Hacer predeterminada
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteConfirm}
        onOpenChange={() => setDeleteConfirm(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Eliminar Moneda
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              ¿Estás seguro de que deseas eliminar esta moneda?
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              disabled={loading.delete}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteCurrency(deleteConfirm!)}
              disabled={loading.delete}
            >
              {loading.delete ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
